import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { footerData } from '../data/mockData';

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
    bookingType: 'consultation' // consultation or general inquiry
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Simulate form submission (will be replaced with actual EmailJS integration)
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        service: '',
        message: '',
        bookingType: 'consultation'
      });

      // Reset success message after 5 seconds
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 1500);
  };

  const serviceOptions = [
    'Brand Strategy & Identity',
    'Performance Marketing',
    'Website Design & Development',
    'Content & Video Creation',
    'Amazon & Marketplaces',
    'Product Launch & Market Strategy',
    'Social Media Marketing',
    'Medical Writing & Communication'
  ];

  return (
    <div className="bg-[rgb(17,17,19)] min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block px-6 py-2 bg-[rgba(0,250,99,0.1)] border border-[rgba(0,250,99,0.3)] rounded-full mb-6">
              <span className="text-[rgb(0,250,99)] text-sm font-medium">
                Get In Touch
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Let's Start a Conversation
            </h1>
            <p className="text-xl text-[rgb(218,218,218)]">
              Ready to grow your healthcare brand? Share your details and we'll get back to you within 24 hours
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-24">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">
                  Get in Touch
                </h2>
                <p className="text-[rgb(218,218,218)] leading-relaxed">
                  Whether you're looking to launch a new product, scale your brand, or improve your digital presence, we're here to help. Let's discuss how we can support your goals.
                </p>
              </div>

              {/* Contact Cards */}
              <div className="space-y-4">
                <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-6 hover:border-[rgb(0,250,99)] transition-colors group">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-[rgba(0,250,99,0.1)] flex items-center justify-center flex-shrink-0 group-hover:bg-[rgb(0,250,99)] transition-colors">
                      <Mail
                        size={24}
                        className="text-[rgb(0,250,99)] group-hover:text-[rgb(17,17,19)] transition-colors"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        Email Us
                      </h3>
                      <a
                        href={`mailto:${footerData.contact.email}`}
                        className="text-[rgb(161,161,170)] hover:text-[rgb(0,250,99)] transition-colors"
                      >
                        {footerData.contact.email}
                      </a>
                    </div>
                  </div>
                </div>

                <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-6 hover:border-[rgb(0,250,99)] transition-colors group">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-[rgba(0,250,99,0.1)] flex items-center justify-center flex-shrink-0 group-hover:bg-[rgb(0,250,99)] transition-colors">
                      <Phone
                        size={24}
                        className="text-[rgb(0,250,99)] group-hover:text-[rgb(17,17,19)] transition-colors"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        Call Us
                      </h3>
                      <p className="text-[rgb(161,161,170)]">
                        {footerData.contact.phone}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-6 hover:border-[rgb(0,250,99)] transition-colors group">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 rounded-xl bg-[rgba(0,250,99,0.1)] flex items-center justify-center flex-shrink-0 group-hover:bg-[rgb(0,250,99)] transition-colors">
                      <MapPin
                        size={24}
                        className="text-[rgb(0,250,99)] group-hover:text-[rgb(17,17,19)] transition-colors"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-2">
                        Visit Us
                      </h3>
                      <p className="text-[rgb(161,161,170)]">
                        {footerData.contact.address}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Image */}
              <div className="rounded-2xl overflow-hidden border border-[rgba(255,255,255,0.1)]">
                <img
                  src="https://images.pexels.com/photos/7195308/pexels-photo-7195308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Healthcare consulting"
                  className="w-full h-64 object-cover"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] rounded-2xl p-8">
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-[rgba(0,250,99,0.1)] border border-[rgba(0,250,99,0.3)] rounded-xl">
                  <p className="text-[rgb(0,250,99)] font-medium">
                    Thank you! We'll get back to you within 24 hours.
                  </p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Booking Type */}
                <div>
                  <label className="block text-white font-medium mb-3">
                    I'm interested in:
                  </label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          bookingType: 'consultation'
                        }))
                      }
                      className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                        formData.bookingType === 'consultation'
                          ? 'bg-[rgb(0,250,99)] text-[rgb(17,17,19)]'
                          : 'bg-[rgb(38,40,42)] text-[rgb(218,218,218)] border border-[rgba(255,255,255,0.1)] hover:border-[rgb(0,250,99)]'
                      }`}
                    >
                      Free Consultation
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, bookingType: 'inquiry' }))
                      }
                      className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                        formData.bookingType === 'inquiry'
                          ? 'bg-[rgb(0,250,99)] text-[rgb(17,17,19)]'
                          : 'bg-[rgb(38,40,42)] text-[rgb(218,218,218)] border border-[rgba(255,255,255,0.1)] hover:border-[rgb(0,250,99)]'
                      }`}
                    >
                      General Inquiry
                    </button>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-white font-medium mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] rounded-xl text-white placeholder-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(0,250,99)] transition-colors"
                    placeholder="John Doe"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-white font-medium mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] rounded-xl text-white placeholder-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(0,250,99)] transition-colors"
                    placeholder="[email protected]"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-white font-medium mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] rounded-xl text-white placeholder-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(0,250,99)] transition-colors"
                    placeholder="+91 XXX XXX XXXX"
                  />
                </div>

                {/* Company */}
                <div>
                  <label htmlFor="company" className="block text-white font-medium mb-2">
                    Company / Organization
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] rounded-xl text-white placeholder-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(0,250,99)] transition-colors"
                    placeholder="Your Company Name"
                  />
                </div>

                {/* Service Interest */}
                <div>
                  <label htmlFor="service" className="block text-white font-medium mb-2">
                    Service of Interest
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] rounded-xl text-white focus:outline-none focus:border-[rgb(0,250,99)] transition-colors"
                  >
                    <option value="">Select a service</option>
                    {serviceOptions.map((service) => (
                      <option key={service} value={service}>
                        {service}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-white font-medium mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-3 bg-[rgb(38,40,42)] border border-[rgba(255,255,255,0.1)] rounded-xl text-white placeholder-[rgb(161,161,170)] focus:outline-none focus:border-[rgb(0,250,99)] transition-colors resize-none"
                    placeholder="Tell us about your project or inquiry..."
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex items-center justify-center space-x-2 px-8 py-4 bg-[rgb(0,250,99)] text-[rgb(17,17,19)] rounded-xl font-semibold text-lg hover:bg-[rgb(0,220,87)] transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  {!isSubmitting && <Send size={20} />}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
